import { useSupportUserStore } from 'stores/supportUserStore';

/**
 * Hook for accessing support user state and actions.
 *
 * This hook provides a clean interface for components to check support user status
 * and access support-related functionality.
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { isSupportUser, fullAccessEnabled, canAccessSupportFeatures } = useSupportUser();
 *
 *   if (canAccessSupportFeatures) {
 *     return <SupportOnlyFeature />;
 *   }
 *   return <RegularContent />;
 * }
 * ```
 */
export function useSupportUser() {
  const isSupportUser = useSupportUserStore((state) => state.isSupportUser);
  const fullAccessEnabled = useSupportUserStore(
    (state) => state.fullAccessEnabled
  );
  const impersonatedEmail = useSupportUserStore(
    (state) => state.impersonatedEmail
  );

  // Actions
  const toggleFullAccess = useSupportUserStore(
    (state) => state.toggleFullAccess
  );
  const enableFullAccess = useSupportUserStore(
    (state) => state.enableFullAccess
  );
  const disableFullAccess = useSupportUserStore(
    (state) => state.disableFullAccess
  );
  const setImpersonatedEmail = useSupportUserStore(
    (state) => state.setImpersonatedEmail
  );
  const setSupportUser = useSupportUserStore((state) => state.setSupportUser);
  const reset = useSupportUserStore((state) => state.reset);

  return {
    // State
    /**
     * Whether the current session is a support user (support@salv.ai or support@tenzo.ai).
     */
    isSupportUser,

    /**
     * Whether Full Access mode is enabled.
     * When true, support user has Admin permissions in the impersonated user's org.
     * When false, support user sees exactly what the impersonated user sees.
     */
    fullAccessEnabled,

    /**
     * The email of the user being impersonated.
     */
    impersonatedEmail,

    // Derived state
    /**
     * Convenience property: True if support user is in Full Access mode.
     * This is when support-only features (Billing, Feedback, etc.) should be visible.
     */
    canAccessSupportFeatures: isSupportUser && fullAccessEnabled,

    /**
     * Whether support mode is active (support user with impersonated email set).
     * This indicates the support user is operating in an org context.
     */
    supportModeActive: isSupportUser && !!impersonatedEmail,

    // Actions
    /**
     * Toggle Full Access mode on/off.
     */
    toggleFullAccess,

    /**
     * Enable Full Access mode explicitly.
     */
    enableFullAccess,

    /**
     * Disable Full Access mode explicitly.
     */
    disableFullAccess,

    /**
     * Set the email of the user being impersonated.
     */
    setImpersonatedEmail,

    /**
     * Mark the current session as a support user session.
     */
    setSupportUser,

    /**
     * Reset all support user state.
     */
    reset,
  };
}
